import { useState, useEffect } from "react";

const API = process.env.REACT_APP_API_URL || "http://localhost:5001";
const token = () => localStorage.getItem("adminToken");

const EMPTY_FORM = {
  code: "", type: "percentage", value: "", min_order_value: "",
  max_uses: "", expires_at: "", is_active: true,
};

function Badge({ label, color }) {
  const map = {
    green:  "bg-green-100 text-green-700",
    red:    "bg-red-100 text-red-700",
    gray:   "bg-gray-100 text-gray-500",
    amber:  "bg-amber-100 text-amber-700",
  };
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${map[color]}`}>
      {label}
    </span>
  );
}

function DiscountModal({ discount, onClose, onSaved }) {
  const [form, setForm] = useState(discount || EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const isEdit = !!discount;

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const url    = isEdit ? `${API}/api/admin/discounts/${discount.id}` : `${API}/api/admin/discounts`;
      const method = isEdit ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({
          ...form,
          value:           parseFloat(form.value),
          min_order_value: form.min_order_value ? parseFloat(form.min_order_value) : 0,
          max_uses:        form.max_uses ? parseInt(form.max_uses) : null,
          expires_at:      form.expires_at || null,
          is_active:       form.is_active ? 1 : 0,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message || "Error saving discount."); return; }
      onSaved(data, isEdit);
      onClose();
    } catch {
      setError("Could not connect to server.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-end">
      <div className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-gray-900">{isEdit ? "Edit Discount" : "Create Discount Code"}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3">{error}</div>
          )}

          {/* Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Discount Code *</label>
            <input
              value={form.code}
              onChange={e => set("code", e.target.value.toUpperCase())}
              required placeholder="e.g. SAGAR20"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:border-rose-400"
            />
            <p className="text-xs text-gray-400 mt-1">Customers enter this at checkout</p>
          </div>

          {/* Type + Value */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Discount Type *</label>
              <select value={form.type} onChange={e => set("type", e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400">
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed Amount ($)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Value * {form.type === "percentage" ? "(%)" : "($)"}
              </label>
              <input
                type="number" value={form.value}
                onChange={e => set("value", e.target.value)}
                required min="0" step="0.01"
                max={form.type === "percentage" ? 100 : undefined}
                placeholder={form.type === "percentage" ? "e.g. 20" : "e.g. 10"}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400"
              />
            </div>
          </div>

          {/* Min Order + Max Uses */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Order Value ($)</label>
              <input
                type="number" value={form.min_order_value}
                onChange={e => set("min_order_value", e.target.value)}
                min="0" placeholder="0 = no minimum"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Uses</label>
              <input
                type="number" value={form.max_uses}
                onChange={e => set("max_uses", e.target.value)}
                min="1" placeholder="Leave blank = unlimited"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400"
              />
            </div>
          </div>

          {/* Expiry */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
            <input
              type="datetime-local" value={form.expires_at || ""}
              onChange={e => set("expires_at", e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-400"
            />
            <p className="text-xs text-gray-400 mt-1">Leave blank for no expiry</p>
          </div>

          {/* Active toggle */}
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">Active</p>
                <p className="text-xs text-gray-400">Customers can use this code at checkout</p>
              </div>
              <button type="button" onClick={() => set("is_active", !form.is_active)}
                className={`relative w-12 h-6 rounded-full transition-colors duration-200 focus:outline-none ${form.is_active ? "bg-emerald-500" : "bg-gray-300"}`}>
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${form.is_active ? "translate-x-7" : "translate-x-1"}`} />
              </button>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-700 rounded-lg py-2.5 text-sm font-medium hover:bg-gray-50 transition">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white rounded-lg py-2.5 text-sm font-bold transition">
              {loading ? "Saving..." : isEdit ? "Update Code" : "Create Code"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Discounts() {
  const [discounts, setDiscounts] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState("");
  const [modal,     setModal]     = useState(null);
  const [deleting,  setDeleting]  = useState(null);

  useEffect(() => { fetchDiscounts(); }, []);

  async function fetchDiscounts() {
    try {
      const res  = await fetch(`${API}/api/admin/discounts`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await res.json();
      setDiscounts(data);
    } catch {
      setError("Failed to load discounts.");
    } finally {
      setLoading(false);
    }
  }

  function onSaved(discount, isEdit) {
    if (isEdit) {
      setDiscounts(ds => ds.map(d => d.id === discount.id ? discount : d));
    } else {
      setDiscounts(ds => [discount, ...ds]);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this discount code?")) return;
    setDeleting(id);
    try {
      await fetch(`${API}/api/admin/discounts/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token()}` },
      });
      setDiscounts(ds => ds.filter(d => d.id !== id));
    } catch {
      alert("Failed to delete.");
    } finally {
      setDeleting(null);
    }
  }

  async function handleToggle(discount) {
    try {
      const res = await fetch(`${API}/api/admin/discounts/${discount.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ ...discount, is_active: discount.is_active ? 0 : 1 }),
      });
      const data = await res.json();
      setDiscounts(ds => ds.map(d => d.id === discount.id ? data : d));
    } catch {
      alert("Failed to update.");
    }
  }

  function isExpired(expires_at) {
    if (!expires_at) return false;
    return new Date(expires_at) < new Date();
  }

  function getStatus(d) {
    if (!d.is_active) return <Badge label="Inactive" color="gray" />;
    if (isExpired(d.expires_at)) return <Badge label="Expired" color="red" />;
    if (d.max_uses && d.used_count >= d.max_uses) return <Badge label="Used Up" color="red" />;
    return <Badge label="Active" color="green" />;
  }

  const activeCount = discounts.filter(d => d.is_active && !isExpired(d.expires_at)).length;

  if (loading) return (
    <div className="flex items-center justify-center h-full min-h-screen">
      <div className="text-gray-400 text-sm animate-pulse">Loading discounts...</div>
    </div>
  );

  return (
    <div className="p-6 max-w-screen-xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Discounts</h1>
          <p className="text-gray-500 text-sm mt-1">
            {discounts.length} total &nbsp;·&nbsp;
            <span className="text-emerald-600 font-medium">{activeCount} active</span>
          </p>
        </div>
        <button onClick={() => setModal("add")}
          className="bg-rose-600 hover:bg-rose-500 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition">
          + Create Code
        </button>
      </div>

      {error && <div className="bg-red-50 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: "Total Codes",  value: discounts.length,                                        color: "text-gray-900"    },
          { label: "Active Codes", value: activeCount,                                             color: "text-emerald-600" },
          { label: "Total Uses",   value: discounts.reduce((s, d) => s + (d.used_count || 0), 0), color: "text-rose-600"    },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">{label}</p>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-5 py-3 text-left">Code</th>
                <th className="px-5 py-3 text-left">Discount</th>
                <th className="px-5 py-3 text-left">Min Order</th>
                <th className="px-5 py-3 text-left">Uses</th>
                <th className="px-5 py-3 text-left">Expires</th>
                <th className="px-5 py-3 text-left">Status</th>
                <th className="px-5 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {discounts.map(d => (
                <tr key={d.id} className="hover:bg-gray-50 transition">
                  <td className="px-5 py-3">
                    <span className="font-mono font-bold text-gray-900 bg-gray-100 px-2 py-1 rounded text-sm">
                      {d.code}
                    </span>
                  </td>
                  <td className="px-5 py-3 font-semibold text-rose-600">
                    {d.type === "percentage"
                      ? `${d.value}% off`
                      : `$${Number(d.value).toLocaleString()} off`}
                  </td>
                  <td className="px-5 py-3 text-gray-600">
                    {d.min_order_value > 0
                      ? `$${Number(d.min_order_value).toLocaleString()}`
                      : <span className="text-gray-300">None</span>}
                  </td>
                  <td className="px-5 py-3 text-gray-600">
                    {d.used_count}
                    {d.max_uses
                      ? <span className="text-gray-400"> / {d.max_uses}</span>
                      : <span className="text-gray-300"> / ∞</span>}
                  </td>
                  <td className="px-5 py-3 text-gray-600">
                    {d.expires_at
                      ? <span className={isExpired(d.expires_at) ? "text-red-500" : ""}>{new Date(d.expires_at).toLocaleDateString()}</span>
                      : <span className="text-gray-300">Never</span>}
                  </td>
                  <td className="px-5 py-3">{getStatus(d)}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setModal(d)}
                        className="text-blue-600 hover:text-blue-800 text-xs font-medium px-2 py-1 rounded hover:bg-blue-50 transition">
                        Edit
                      </button>
                      <button onClick={() => handleToggle(d)}
                        className={`text-xs font-medium px-2 py-1 rounded transition ${
                          d.is_active
                            ? "text-amber-600 hover:text-amber-800 hover:bg-amber-50"
                            : "text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50"
                        }`}>
                        {d.is_active ? "Disable" : "Enable"}
                      </button>
                      <button onClick={() => handleDelete(d.id)} disabled={deleting === d.id}
                        className="text-red-500 hover:text-red-700 text-xs font-medium px-2 py-1 rounded hover:bg-red-50 transition disabled:opacity-50">
                        {deleting === d.id ? "..." : "Delete"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {discounts.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-16 text-center">
                    <p className="text-gray-400 text-sm mb-3">No discount codes yet</p>
                    <button onClick={() => setModal("add")}
                      className="text-rose-600 text-sm font-semibold hover:underline">
                      Create your first code →
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <DiscountModal
          discount={modal === "add" ? null : modal}
          onClose={() => setModal(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}